import { useState } from "react";
import { UploadCloud, BookOpen } from "lucide-react";
import { useTeamFiles } from "@/lib/teamRecordQueries";
import { useFileUploadContext } from "@/lib/fileQueries";
import { useTeamPractice } from "@/lib/teamRecordQueries";
import FileUploadDialog from "@/pages/RehearsalRoom/files/FileUploadDialog";
import AssignFromLibraryModal from "../AssignFromLibraryModal";

/**
 * Clients Restructure — Team 360 -> Files & Videos. Team/shared files
 * only (owner_member_id=null) — a member's private file never shows
 * here. "Add from Library" reuses the same team-Assignment mechanism
 * as Practice & Layers' "+ Assign Practice" (see AssignFromLibraryModal)
 * so a Library resource shared with the team is visible from both places.
 */
export default function FilesVideosTab({ workspaceId }) {
  const { data, isPending, isError } = useTeamFiles(workspaceId);
  const { data: uploadContext } = useFileUploadContext(workspaceId);
  const { data: practice } = useTeamPractice(workspaceId);
  const [showUpload, setShowUpload] = useState(false);
  const [showFromLibrary, setShowFromLibrary] = useState(false);
  const items = data?.items || [];
  const libraryItems = practice?.items || [];

  return (
    <div data-testid="team-360-files-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-lg text-ink">Files & Videos</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFromLibrary(true)}
            className="text-sm font-medium border border-ink/15 px-4 py-2 rounded-full text-ink hover:bg-ink/5 transition-colors flex items-center gap-1.5"
            data-testid="team-files-add-from-library-btn"
          >
            <BookOpen size={14} /> Add from Library
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 transition-colors flex items-center gap-1.5"
            data-testid="team-files-add-file-btn"
          >
            <UploadCloud size={14} /> Add File
          </button>
        </div>
      </div>

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="team-files-loading">Loading files…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="team-files-error">Couldn't load team files.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="team-files-empty">No team files yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="team-files-list">
          {items.map((f) => (
            <div key={f.fas_id} className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`team-file-${f.fas_id}`}>
              <p className="text-sm text-ink truncate">{f.original_filename}</p>
              <p className="text-xs text-ink-soft uppercase">{f.kind}</p>
            </div>
          ))}
        </div>
      )}

      {libraryItems.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">From Library</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="team-files-from-library-list">
            {libraryItems.map((a) => (
              <div key={a.asg_id} className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`team-files-from-library-${a.asg_id}`}>
                <p className="text-sm text-ink truncate">{a.title_snapshot}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showUpload && (
        <FileUploadDialog
          open onOpenChange={setShowUpload} workspaceId={workspaceId} actorRole="coach"
          isTeamWorkspace teamMembers={uploadContext?.team_members || []}
        />
      )}
      {showFromLibrary && <AssignFromLibraryModal workspaceId={workspaceId} onClose={() => setShowFromLibrary(false)} />}
    </div>
  );
}
